import { WindowTypeEnum } from '@ben/api/gen/codegen.schemas';
import { parseDateValue } from '@ben/common/date';
import Button from '@ben/orion/button';
import type { DateInputRangeValue } from '@ben/orion/date-input';
import DateInput from '@ben/orion/date-input';
import Icon, { IconName } from '@ben/orion/icon';
import InlineTextButton from '@ben/orion/inline-text-button';
import Input from '@ben/orion/input';
import MultiSelect from '@ben/orion/multi-select';
import RadioButton from '@ben/orion/radio-button';
import Select from '@ben/orion/select';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import type { ITheme } from '@ben/orion/utils/theme';
import {
  type IBenefitWindow,
  type IModularBenefitDetail,
} from '@ben/shared/types/columbia';
import { StartsOnEnum } from '@ben/shared/types/modular-benefit';
import { TimeInput } from '@mantine/dates';
import { add, format } from 'date-fns';
import { isEmpty } from 'lodash';
import { memo, useEffect, useState } from 'react';
import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import type { ITimezone } from 'react-timezone-select';
import { allTimezones, useTimezoneSelect } from 'react-timezone-select';
import styled, { css, useTheme } from 'styled-components';
import {
  TIME_UNIT_OPTIONS,
  fieldCannotBeBlankErrorMessage,
} from '../../shared/constants';
import { useSetDirtyValue } from '../hooks/use-set-dirty-value';
import { AirtableImportFeedback } from '../shared/airtable-import-feedback';
import { WindowTriggerType } from './types';

const emptyString = undefined;
const minDate = new Date();
const maxDate = add(new Date(), { years: 20 });

const ENROLMENT_STATUS_OPTIONS = [
  { value: 'DRAFT', label: 'Draft' },
  {
    value: 'WAITING_ACTIVATION_APPROVAL',
    label: 'Waiting activation approval',
  },
  { value: 'PENDING_ACTIVATION', label: 'Pending activation' },
  { value: 'ACTIVE', label: 'Active' },
  {
    value: 'WAITING_DEACTIVATION_APPROVAL',
    label: 'Waiting deactivation approval',
  },
  { value: 'PENDING_DEACTIVATION', label: 'Pending deactivation' },
  { value: 'DEACTIVATED', label: 'Deactivated' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'OPTED_OUT', label: 'Opted out' },
];

export const emptyBenefitWindow: IBenefitWindow = {
  id: undefined,
  endsAfterDays: emptyString,
  startsOn: StartsOnEnum.LIFE_EVENT,
  windowType: WindowTypeEnum.CONFIG_WINDOW,
  startsOnSpecificDate: emptyString,
  endsOnSpecificDate: emptyString,
  startsOnSpecificTime: emptyString,
  endsOnSpecificTime: emptyString,
  timezone: 'Etc/GMT',
  endsAfter: emptyString,
  timeUnit: emptyString,
  ageThreshold: emptyString,
  applicableEnrolmentStatuses: [],
};

interface LifeEventWindowsProps {
  lifeEventIndex: number;
}

const LifeEventWindows = ({ lifeEventIndex }: LifeEventWindowsProps) => {
  const theme = useTheme();

  const { options, parseTimezone } = useTimezoneSelect({
    labelStyle: 'original',
    timezones: allTimezones,
  });

  const { control, getFieldState, setValue } =
    useFormContext<IModularBenefitDetail>();
  const benefitWindows = useWatch({
    name: `lifeEventPolicies.${lifeEventIndex}.windows`,
    exact: true,
  });
  const setDirtyValue = useSetDirtyValue<IModularBenefitDetail>();

  const { fields, append, remove } = useFieldArray({
    name: `lifeEventPolicies.${lifeEventIndex}.windows`,
    control,
  });

  const startsOnValues = useWatch({
    name: fields.map(
      (_, index) =>
        `lifeEventPolicies.${lifeEventIndex}.windows.${index}.startsOn` as const
    ),
  });

  const [windowTriggerTypes, setWindowTriggerTypes] = useState<
    WindowTriggerType[]
  >([]);
  const [expandedWindows, setExpandedWindows] = useState<boolean[]>([]);

  useEffect(() => {
    if (benefitWindows) {
      const initialTriggerTypes = benefitWindows.map(
        (window: IBenefitWindow) =>
          window.startsOnSpecificDate && window.endsOnSpecificDate
            ? WindowTriggerType.DATE_RANGE
            : WindowTriggerType.RULE_BASED
      );
      setWindowTriggerTypes(initialTriggerTypes);

      // Initialize all windows as expanded
      setExpandedWindows(benefitWindows.map(() => true));
    }
  }, [benefitWindows]);

  useEffect(() => {
    if (benefitWindows && !isEmpty(startsOnValues)) {
      for (const [index, window] of benefitWindows.entries()) {
        if (
          startsOnValues[index] !== StartsOnEnum.AGE_THRESHOLD &&
          window.ageThreshold != null
        ) {
          setDirtyValue(
            `lifeEventPolicies.${lifeEventIndex}.windows.${index}.ageThreshold`,
            null
          );
        }
      }
    }
  }, [benefitWindows, startsOnValues, setDirtyValue, lifeEventIndex]);

  const addNewWindow = () => {
    if (fields.length === 0) {
      setValue(`lifeEventPolicies.${lifeEventIndex}.windows`, []);
    }
    append(emptyBenefitWindow);
    setWindowTriggerTypes((previous) => [
      ...previous,
      WindowTriggerType.RULE_BASED,
    ]);
    setExpandedWindows((previous) => [...previous, true]);
  };

  const handleToggleWindow = (index: number) => {
    setExpandedWindows((previous) => {
      const newExpanded = [...previous];
      newExpanded[index] = !newExpanded[index];
      return newExpanded;
    });
  };

  const startsOnOptions = [
    { value: StartsOnEnum.EMPLOYMENT_START, label: 'Employment start' },
    { value: StartsOnEnum.ELIGIBILITY_START, label: 'Eligibility start' },
    { value: StartsOnEnum.PROBATION_END, label: 'Probation end' },
    {
      value: StartsOnEnum.POSTPONEMENT_END,
      label: 'Postponement window end date',
    },
    { value: StartsOnEnum.OPT_IN, label: 'Opt in' },
    { value: StartsOnEnum.ENROLLMENT_END, label: 'Enrollment end' },
    { value: StartsOnEnum.BENEFIT_START_DATE, label: 'Benefit start date' },
    { value: StartsOnEnum.LIFE_EVENT, label: 'Life event' },
    { value: StartsOnEnum.AGE_THRESHOLD, label: 'Age Threshold' },
  ];

  return (
    <Wrapper>
      <HeaderWrapper>
        <Typography variant={TypographyVariant.h3}>Windows</Typography>
        <Button variant="outline" onClick={addNewWindow}>
          Add life event window
        </Button>
      </HeaderWrapper>
      {fields?.length === 0 && (
        <AddAndNoSubPriceWrapper>
          <Typography variant={TypographyVariant.bodyLg1}>
            No windows added yet
          </Typography>
        </AddAndNoSubPriceWrapper>
      )}

      {fields.map((benefitWindow, index) => {
        const { isDirty } = getFieldState(
          `lifeEventPolicies.${lifeEventIndex}.windows.${index}`
        );
        const windowTriggerType = windowTriggerTypes[index];
        const startsOnSpecificDate =
          benefitWindows?.[index]?.startsOnSpecificDate;
        const endsOnSpecificDate = benefitWindows?.[index]?.endsOnSpecificDate;
        const dateRangeValue =
          !!startsOnSpecificDate && !!endsOnSpecificDate
            ? ([
                parseDateValue(startsOnSpecificDate),
                parseDateValue(endsOnSpecificDate),
              ] as DateInputRangeValue)
            : ([undefined, undefined] as unknown as undefined);

        const isExpanded = expandedWindows[index];

        return (
          <ColumnWrapper key={benefitWindow.id}>
            <WindowHeaderWrapper onClick={() => handleToggleWindow(index)}>
              <WindowHeader>
                <WindowHeaderIconTextWrapper>
                  <Chevron $isOpen={isExpanded} name={IconName.ArrowsDown} />
                  <Typography variant={TypographyVariant.h3}>
                    Window #{index + 1}
                  </Typography>
                </WindowHeaderIconTextWrapper>
                <StyledInlineTextButton
                  label="Remove"
                  aria-label="Remove"
                  onClick={(event) => {
                    event.stopPropagation();
                    remove(index);
                    setWindowTriggerTypes((previous) =>
                      previous.filter((_, index_) => index_ !== index)
                    );
                    setExpandedWindows((previous) =>
                      previous.filter((_, index_) => index_ !== index)
                    );
                  }}
                />
              </WindowHeader>
            </WindowHeaderWrapper>
            <AirtableImportFeedback
              visible={!!benefitWindow.importedFromAirtable && !isDirty}
            />

            {isExpanded && (
              <>
                {/* Window Type */}
                <Section>
                  <Typography variant={TypographyVariant.bodyLg2}>
                    Window type
                  </Typography>
                  <Controller
                    name={`lifeEventPolicies.${lifeEventIndex}.windows.${index}.windowType`}
                    control={control}
                    render={({ field }) => {
                      const { value, ...rest } = field;
                      return (
                        <WindowTypeRowWrapper>
                          <RadioButton
                            key={`config-window-${index}`}
                            {...rest}
                            label="Config window"
                            checked={
                              !value || value === WindowTypeEnum.CONFIG_WINDOW
                            }
                            backgroundColor={theme.palette.neutral['00']}
                            value={WindowTypeEnum.CONFIG_WINDOW}
                          />
                          <RadioButton
                            key={`opt-in-${index}`}
                            {...rest}
                            label="Opt-in"
                            checked={value === WindowTypeEnum.OPT_IN_WINDOW}
                            backgroundColor={theme.palette.neutral['00']}
                            value={WindowTypeEnum.OPT_IN_WINDOW}
                          />
                          <RadioButton
                            key={`opt-out-${index}`}
                            {...rest}
                            label="Opt-out"
                            checked={value === WindowTypeEnum.OPT_OUT_WINDOW}
                            backgroundColor={theme.palette.neutral['00']}
                            value={WindowTypeEnum.OPT_OUT_WINDOW}
                          />
                          <RadioButton
                            key={`locked-window-${index}`}
                            {...rest}
                            label="Locked"
                            checked={value === WindowTypeEnum.LOCKED_WINDOW}
                            backgroundColor={theme.palette.neutral['00']}
                            value={WindowTypeEnum.LOCKED_WINDOW}
                          />
                        </WindowTypeRowWrapper>
                      );
                    }}
                  />
                </Section>

                <Section>
                  <Typography variant={TypographyVariant.bodyLg2}>
                    Applicable enrolment statuses
                  </Typography>
                  <Controller
                    name={`lifeEventPolicies.${lifeEventIndex}.windows.${index}.applicableEnrolmentStatuses`}
                    control={control}
                    render={({ field }) => (
                      <MultiSelect
                        {...field}
                        label="Enrolment statuses"
                        data={ENROLMENT_STATUS_OPTIONS}
                        description="Leave empty to apply to all enrolment statuses."
                        width="100%"
                      />
                    )}
                  />
                </Section>

                <Section>
                  <Typography variant={TypographyVariant.bodyLg2}>
                    Set opening & close
                  </Typography>
                  <RadioButton
                    key={`rule-based-${index}`}
                    label="Rule based"
                    checked={windowTriggerType === WindowTriggerType.RULE_BASED}
                    backgroundColor={theme.palette.neutral['00']}
                    onChange={() => {
                      const newTriggerTypes = [...windowTriggerTypes];
                      newTriggerTypes[index] = WindowTriggerType.RULE_BASED;
                      setWindowTriggerTypes(newTriggerTypes);
                      setDirtyValue(
                        `lifeEventPolicies.${lifeEventIndex}.windows.${index}.startsOnSpecificDate`,
                        null
                      );
                      setDirtyValue(
                        `lifeEventPolicies.${lifeEventIndex}.windows.${index}.endsOnSpecificDate`,
                        null
                      );
                      setDirtyValue(
                        `lifeEventPolicies.${lifeEventIndex}.windows.${index}.startsOnSpecificTime`,
                        null
                      );
                      setDirtyValue(
                        `lifeEventPolicies.${lifeEventIndex}.windows.${index}.endsOnSpecificTime`,
                        null
                      );
                    }}
                  />
                  <RowWrapper>
                    {/* Starts On */}
                    <Controller
                      name={`lifeEventPolicies.${lifeEventIndex}.windows.${index}.startsOn`}
                      control={control}
                      render={({ field }) => (
                        <StyledSelectWithMargin
                          {...field}
                          data={startsOnOptions}
                          disabled={
                            windowTriggerType === WindowTriggerType.DATE_RANGE
                          }
                          label="Starts on"
                        />
                      )}
                    />
                    <Controller
                      name={`lifeEventPolicies.${lifeEventIndex}.windows.${index}.endsAfter`}
                      control={control}
                      rules={{
                        required: {
                          value:
                            WindowTriggerType.RULE_BASED === windowTriggerType,
                          message: fieldCannotBeBlankErrorMessage,
                        },
                      }}
                      render={({ field, fieldState }) => {
                        const { value, ...rest } = field;
                        return (
                          <Input
                            key={String(benefitWindow.id ?? '') + 'days'}
                            {...rest}
                            name="Ends after"
                            type="number"
                            value={value ?? ''}
                            hasError={
                              windowTriggerTypes[index] ===
                                WindowTriggerType.RULE_BASED &&
                              !!fieldState.error
                            }
                            disabled={
                              windowTriggerType === WindowTriggerType.DATE_RANGE
                            }
                            errorMessage={fieldState.error?.message}
                            width="240px"
                            hintText="Number of days the window is open after the 'Start on' date."
                          />
                        );
                      }}
                    />
                    <Controller
                      name={`lifeEventPolicies.${lifeEventIndex}.windows.${index}.timeUnit`}
                      control={control}
                      rules={{
                        required: {
                          value:
                            WindowTriggerType.RULE_BASED === windowTriggerType,
                          message: fieldCannotBeBlankErrorMessage,
                        },
                      }}
                      render={({ field, fieldState }) => {
                        const { value, ...rest } = field;
                        return (
                          <StyledSelectWithMargin
                            {...rest}
                            value={value}
                            label="Time unit"
                            data-testid="time-unit-select"
                            data={TIME_UNIT_OPTIONS.map((option) => ({
                              value: option.key,
                              label: option.label,
                            }))}
                            disabled={
                              windowTriggerType === WindowTriggerType.DATE_RANGE
                            }
                            errorMessage={fieldState.error?.message}
                          />
                        );
                      }}
                    />
                    {startsOnValues?.[index] === StartsOnEnum.AGE_THRESHOLD && (
                      <Controller
                        name={`lifeEventPolicies.${lifeEventIndex}.windows.${index}.ageThreshold`}
                        control={control}
                        rules={{
                          required: {
                            value:
                              startsOnValues?.[index] ===
                              StartsOnEnum.AGE_THRESHOLD,
                            message: fieldCannotBeBlankErrorMessage,
                          },
                        }}
                        render={({ field, fieldState }) => {
                          const { value, ...rest } = field;
                          return (
                            <Input
                              key={
                                String(benefitWindow.id ?? '') + 'ageThreshold'
                              }
                              {...rest}
                              name="Age Threshold"
                              type="number"
                              value={value ?? ''}
                              hasError={!!fieldState.error}
                              errorMessage={fieldState.error?.message}
                              width="240px"
                              data-testid="age-threshold-input"
                              hintText="Age at which the window opens."
                            />
                          );
                        }}
                      />
                    )}
                  </RowWrapper>
                  <DateRangeRowWrapper>
                    <div>
                      <RadioButton
                        key={`date-range-${index}`}
                        label="Date range"
                        checked={
                          windowTriggerType === WindowTriggerType.DATE_RANGE
                        }
                        backgroundColor={theme.palette.neutral['00']}
                        onChange={() => {
                          const newTriggerTypes = [...windowTriggerTypes];
                          newTriggerTypes[index] = WindowTriggerType.DATE_RANGE;
                          setWindowTriggerTypes(newTriggerTypes);
                          setDirtyValue(
                            `lifeEventPolicies.${lifeEventIndex}.windows.${index}.endsAfter`,
                            null
                          );
                          setDirtyValue(
                            `lifeEventPolicies.${lifeEventIndex}.windows.${index}.timeUnit`,
                            null
                          );
                          setDirtyValue(
                            `lifeEventPolicies.${lifeEventIndex}.windows.${index}.startsOn`,
                            StartsOnEnum.SPECIFIC_DATE
                          );
                          setDirtyValue(
                            `lifeEventPolicies.${lifeEventIndex}.windows.${index}.startsOnSpecificTime`,
                            '00:00:00'
                          );
                          setDirtyValue(
                            `lifeEventPolicies.${lifeEventIndex}.windows.${index}.endsOnSpecificTime`,
                            '23:59:59'
                          );
                        }}
                      />
                      <DateInput<'range'>
                        type="range"
                        label="Specify date range"
                        maxDate={maxDate}
                        minDate={minDate}
                        disabled={
                          windowTriggerType === WindowTriggerType.RULE_BASED
                        }
                        defaultValue={dateRangeValue}
                        onChange={([startDate, endDate]) => {
                          setDirtyValue(
                            `lifeEventPolicies.${lifeEventIndex}.windows.${index}.startsOnSpecificDate`,
                            startDate
                              ? format(startDate, 'yyyy-MM-dd')
                              : undefined
                          );
                          setDirtyValue(
                            `lifeEventPolicies.${lifeEventIndex}.windows.${index}.endsOnSpecificDate`,
                            endDate ? format(endDate, 'yyyy-MM-dd') : undefined
                          );
                        }}
                        errorMessage={
                          windowTriggerType === WindowTriggerType.DATE_RANGE &&
                          (!startsOnSpecificDate || !endsOnSpecificDate)
                            ? fieldCannotBeBlankErrorMessage
                            : undefined
                        }
                      />
                      <RowWrapper>
                        <Controller
                          name={`lifeEventPolicies.${lifeEventIndex}.windows.${index}.startsOnSpecificTime`}
                          control={control}
                          render={({ field }) => (
                            <TimeInput
                              label="Starts on time"
                              withSeconds
                              w={160}
                              disabled={
                                windowTriggerType ===
                                WindowTriggerType.RULE_BASED
                              }
                              value={field.value ?? ''}
                              onChange={(event) => {
                                field.onChange(
                                  event.currentTarget.value || null
                                );
                              }}
                            />
                          )}
                        />
                        <Controller
                          name={`lifeEventPolicies.${lifeEventIndex}.windows.${index}.endsOnSpecificTime`}
                          control={control}
                          render={({ field }) => (
                            <TimeInput
                              label="Ends on time"
                              withSeconds
                              w={160}
                              disabled={
                                windowTriggerType ===
                                WindowTriggerType.RULE_BASED
                              }
                              value={field.value ?? ''}
                              onChange={(event) => {
                                field.onChange(
                                  event.currentTarget.value || null
                                );
                              }}
                            />
                          )}
                        />
                      </RowWrapper>
                    </div>
                    <Controller
                      name={`lifeEventPolicies.${lifeEventIndex}.windows.${index}.timezone`}
                      control={control}
                      render={({ field }) => {
                        const { value, ref, ...rest } = field;
                        return (
                          <StyledSelect
                            {...rest}
                            data={options}
                            label="Timezone"
                            value={value}
                            onChange={(value) => {
                              const parsedTimezone = parseTimezone(
                                value as ITimezone
                              );
                              if (parsedTimezone) {
                                setDirtyValue(
                                  `lifeEventPolicies.${lifeEventIndex}.windows.${index}.timezone`,
                                  parsedTimezone.value
                                );
                              }
                            }}
                            searchable
                          />
                        );
                      }}
                    />
                  </DateRangeRowWrapper>
                </Section>
              </>
            )}
          </ColumnWrapper>
        );
      })}
    </Wrapper>
  );
};

export default memo(LifeEventWindows);

const ColumnWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    border-radius: 10px;
    background-color: ${theme.palette.neutral['10']};
    border: 1px solid ${theme.palette.primary.main};
    padding: ${theme.spacing(3)};
    gap: ${theme.spacing(3)};
  `}
`;

const Section = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `}
`;

const RowWrapper = styled.div`
  ${({ theme, center }: { theme: ITheme; center?: boolean }) => css`
    display: flex;
    gap: ${theme.spacing(2.5)};
    align-items: ${center ? 'center' : 'flex-start'};
  `}
`;

const WindowTypeRowWrapper = styled.div`
  ${({ theme }) => css`
    gap: ${theme.spacing(3)};
    display: flex;
    flex-direction: row;
  `}
`;

const AddAndNoSubPriceWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const Wrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
    width: 100%;
  `}
`;

const StyledInlineTextButton = styled(InlineTextButton)`
  ${({ theme }) => theme.typography.bodyLg2};
`;

const DateRangeRowWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(2.5)};
    align-items: flex-end;
    width: 100%;
  `}
`;

const StyledSelect = styled(Select)`
  width: 335px;
`;

const StyledSelectWithMargin = styled(Select)`
  ${({ theme }) => css`
    margin-top: ${theme.spacing(-1)};
  `}
`;

const WindowHeaderWrapper = styled.div`
  ${({ theme }) => css`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
    cursor: pointer;
  `}
`;

const WindowHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const WindowHeaderIconTextWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(1)};
    align-items: center;
  `}
`;

const Chevron = styled(Icon)<{ $isOpen: boolean }>`
  transform: rotate(${({ $isOpen }) => ($isOpen ? '0deg' : '-90deg')});
  cursor: pointer;
`;
